/**
 * Public opt-out landing. Two flows:
 *   1. Direct query "choice=…" arrives → we auto-apply and confirm.
 *   2. Just "token" arrives → we show the 4-button preference UI.
 *
 * URL:  /broadcast/optout?token=…&choice=keep_in|snooze_60|snooze_90|opt_out_permanent
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Bell, PauseCircle, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CHOICES = [
    {
        key: "keep_in",
        icon: Bell,
        label: "Keep me in — I still want shift offers",
        desc: "You'll continue to receive open-shift emails in your ZIP.",
        tone: "bg-emerald-700 hover:bg-emerald-800 text-white",
    },
    {
        key: "snooze_60",
        icon: PauseCircle,
        label: "Snooze 2 months",
        desc: "We'll pause emails for 60 days, then automatically add you back.",
        tone: "bg-white border border-stone-200 text-stone-800 hover:bg-stone-50",
    },
    {
        key: "snooze_90",
        icon: PauseCircle,
        label: "Snooze 3 months",
        desc: "Pause 90 days, then auto-reactivate. Best for extended breaks.",
        tone: "bg-white border border-stone-200 text-stone-800 hover:bg-stone-50",
    },
    {
        key: "opt_out_permanent",
        icon: X,
        label: "Take me off the list completely",
        desc: "Zero further shift emails. You can log in later to opt back in.",
        tone: "bg-white border border-stone-200 text-stone-500 hover:bg-stone-50",
    },
];

const CHOICE_LABEL = {
    keep_in: "You're back in the labor pool.",
    snooze_60: "Snoozed for 60 days — we'll email you again after that.",
    snooze_90: "Snoozed for 90 days — we'll email you again after that.",
    opt_out_permanent: "You've been removed from all shift emails.",
};

export default function BroadcastOptout() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const initialChoice = params.get("choice");

    const [ctx, setCtx] = useState({ loading: true, error: null, data: null });
    const [applied, setApplied] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) {
            setCtx({ loading: false, error: "Missing token.", data: null });
            return;
        }
        // If choice is in URL, apply it directly; otherwise fetch context.
        const url = initialChoice
            ? `${API}/api/broadcasts/optout?token=${encodeURIComponent(token)}&choice=${initialChoice}`
            : `${API}/api/broadcasts/optout?token=${encodeURIComponent(token)}`;
        fetch(url)
            .then(async (r) => {
                if (!r.ok) {
                    const j = await r.json().catch(() => ({}));
                    throw new Error(j.detail || r.statusText);
                }
                return r.json();
            })
            .then((d) => {
                if (initialChoice) {
                    setApplied(initialChoice);
                    setCtx({ loading: false, error: null, data: d });
                } else {
                    setCtx({ loading: false, error: null, data: d });
                }
            })
            .catch((e) => setCtx({ loading: false, error: e.message, data: null }));
    }, [token, initialChoice]);

    async function apply(choice) {
        setBusy(true);
        try {
            const r = await fetch(
                `${API}/api/broadcasts/optout?token=${encodeURIComponent(token)}&choice=${choice}`
            );
            if (!r.ok) throw new Error("Update failed");
            setApplied(choice);
        } catch (e) {
            alert(e.message);
        } finally {
            setBusy(false);
        }
    }

    if (ctx.loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6">
                <div className="text-sm text-stone-500 font-dmsans">Loading your preferences…</div>
            </div>
        );
    }

    if (ctx.error) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-16">
                <div className="max-w-lg w-full bg-white border-2 border-amber-200 bg-amber-50 rounded-3xl p-10 text-center">
                    <AlertCircle className="w-14 h-14 text-amber-600 mx-auto mb-4" />
                    <h1 className="font-outfit font-semibold text-2xl text-stone-900">Link expired</h1>
                    <p className="mt-3 text-stone-600 font-dmsans">{ctx.error}</p>
                    <Link
                        to="/"
                        className="inline-block mt-6 px-6 py-3 rounded-full bg-stone-900 text-white text-sm font-outfit"
                    >
                        Back to CareCo 24
                    </Link>
                </div>
            </div>
        );
    }

    if (applied) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-16">
                <div className="max-w-lg w-full bg-white border-2 border-emerald-200 bg-emerald-50 rounded-3xl p-10 text-center" data-testid="broadcast-optout-applied">
                    <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
                    <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-stone-500 mb-3">
                        Infini HealthCare · CareCo 24
                    </div>
                    <h1 className="font-outfit font-semibold text-3xl text-stone-900 tracking-tight">
                        Preferences saved
                    </h1>
                    <p className="mt-4 text-stone-700 font-dmsans">{CHOICE_LABEL[applied] || "Your choice has been recorded."}</p>
                    <p className="mt-2 text-xs text-stone-500 font-dmsans">
                        Change your mind anytime — sign in to your caregiver profile and update ZIP coverage + availability.
                    </p>
                    <div className="mt-8">
                        <Link
                            to="/auth?mode=login"
                            className="inline-block px-6 py-3 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-sm font-outfit font-medium"
                        >
                            Sign in to my caregiver profile
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50 px-6 py-16">
            <div className="max-w-xl mx-auto bg-white border border-stone-200 rounded-3xl p-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-stone-500 mb-3">
                    Infini HealthCare · CareCo 24
                </div>
                <h1 className="font-outfit font-semibold text-3xl text-stone-900 tracking-tight">
                    Are you still available for shifts?
                </h1>
                <p className="mt-3 text-stone-600 font-dmsans leading-relaxed">
                    We haven&apos;t heard back from you on our recent open-shift emails. Pick what works best:
                </p>
                <div className="mt-6 space-y-2" data-testid="broadcast-optout-choices">
                    {CHOICES.map((c) => {
                        const Icon = c.icon;
                        return (
                            <button
                                key={c.key}
                                data-testid={`broadcast-optout-${c.key}`}
                                onClick={() => apply(c.key)}
                                disabled={busy}
                                className={`w-full text-left rounded-2xl p-4 flex items-start gap-3 transition-all ${c.tone} disabled:opacity-60`}
                            >
                                <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                <div className="min-w-0">
                                    <div className="font-outfit font-semibold text-sm">{c.label}</div>
                                    <div className={`text-xs font-dmsans mt-0.5 opacity-90 ${c.key === "keep_in" ? "text-emerald-50" : "text-stone-500"}`}>
                                        {c.desc}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
                <p className="mt-6 text-xs text-stone-400 font-dmsans">
                    Signed in as <strong>{ctx.data?.caregiver_email}</strong>. Your identity comes from the email we sent &mdash; no password required.
                </p>
            </div>
        </div>
    );
}
